import { DataIngestionService, IngestionFieldDefinition, IngestionPoint } from "./data.ingestion.service";
import { copyFormStyleWithoutHeaderImage } from "./field-types/form.style.config";
import { RuleTreeService } from "../../services/rule.tree.service";
import { S25RuleTreeUtil } from "../s25-rule-tree/s25.rule.tree.util";
import { Rules } from "../s25-rule-tree/s25.rule.const";

export const COPY_INGESTION_FORM_PREFIX = "Copy of ";
export const MAX_INGESTION_POINT_NAME_LENGTH = 128;
export const MAX_RULE_NAME_LENGTH = 40;

const COPIED_INGESTION_RULE_CATEGORIES: Rules.Category[] = ["ingestion", "ingestionValid"];

export interface CopyIngestionFormOptions {
    /** When set, assigned to the copied form instead of the source subCat. */
    targetSubCat?: string;
}

export function buildCopiedIngestionPointName(originalName: string): string {
    const safeName = originalName || "Untitled Form";
    const maxOriginalLength = MAX_INGESTION_POINT_NAME_LENGTH - COPY_INGESTION_FORM_PREFIX.length;
    const truncatedOriginalName =
        safeName.length > maxOriginalLength ? safeName.substring(0, maxOriginalLength) : safeName;
    return `${COPY_INGESTION_FORM_PREFIX}${truncatedOriginalName}`;
}

export function buildCopiedIngestionRuleName(originalName: string, newIngestionPointId: number): string {
    const prefix = `${newIngestionPointId}: `;
    const baseName = (originalName || "").replace(/^\d+:\s+/, "");
    const maxBaseLength = MAX_RULE_NAME_LENGTH - prefix.length;
    const truncatedBaseName = baseName.length > maxBaseLength ? baseName.substring(0, maxBaseLength) : baseName;
    return `${prefix}${truncatedBaseName}`;
}

function copyFieldDefinitionsForNewForm(fieldDefinitions: IngestionFieldDefinition[]): IngestionFieldDefinition[] {
    return fieldDefinitions
        .filter((fieldDef) => !fieldDef.fieldDefinitionId || fieldDef.fieldDefinitionId >= 0)
        .map((fieldDef) => ({
            // keep the same field definition ID so rules work
            // note: the PK is ingestion_point_id, field_definition_id so this is safe
            fieldDefinitionId: fieldDef.fieldDefinitionId,
            fieldLabel: fieldDef.fieldLabel,
            fieldType: fieldDef.fieldType,
            answerType: fieldDef.answerType,
            infoText: fieldDef.infoText,
            infoTextExpanded: fieldDef.infoTextExpanded ?? false,
            sortOrder: fieldDef.sortOrder,
            hidden: fieldDef.hidden,
            required: fieldDef.required,
            minLength: fieldDef.minLength,
            maxLength: fieldDef.maxLength,
            regexValid: fieldDef.regexValid,
            reportingFieldCode: fieldDef.reportingFieldCode,
            defaultValue: fieldDef.defaultValue,
            derived: fieldDef.derived,
            readOnly: fieldDef.readOnly,
            fieldConfig: fieldDef.fieldConfig,
            requiresReapproval: fieldDef.requiresReapproval,
            custAtrbId: fieldDef.custAtrbId,
            options: fieldDef.options
                ? fieldDef.options.map((option) => ({
                      option: option.option,
                      sortOrder: option.sortOrder,
                  }))
                : undefined,
            newAnswer: { text: "" },
        }));
}

async function copyIngestionRulesForNewForm(
    sourceIngestionPointId: number,
    newIngestionPointId: number,
): Promise<void> {
    const sourceSubCategory = String(sourceIngestionPointId);
    const newSubCategory = String(newIngestionPointId);
    const savePromises: Promise<unknown>[] = [];

    for (const category of COPIED_INGESTION_RULE_CATEGORIES) {
        const rawRules = await RuleTreeService.getRulesBySubCategory(category, sourceSubCategory, true);
        const parsedRules = S25RuleTreeUtil.parseRules(rawRules);
        for (const rule of parsedRules) {
            rule.id = -1 * Math.ceil((Math.random() + 1) * 100000000);
            rule.subCategory = newSubCategory;
            rule.name = buildCopiedIngestionRuleName(rule.name, newIngestionPointId);
            savePromises.push(RuleTreeService.putParsedRule(rule));
        }
    }

    await Promise.all(savePromises);
}

export async function copyIngestionForm(
    sourceIngestionPointId: number,
    options?: CopyIngestionFormOptions,
): Promise<IngestionPoint> {
    const [sourceForm, fieldDefinitions] = await Promise.all([
        DataIngestionService.getIngestionPoint(sourceIngestionPointId),
        DataIngestionService.getFieldDefinitions(sourceIngestionPointId),
    ]);

    if (!sourceForm) {
        throw new Error("Cannot copy form: source form not found");
    }

    const copiedForm: IngestionPoint = {
        ingestionPointName: buildCopiedIngestionPointName(sourceForm.ingestionPointName || ""),
        category: sourceForm.category,
        subCat: options?.targetSubCat ?? sourceForm.subCat,
        description: sourceForm.description,
        successMessage: sourceForm.successMessage,
        formStyle: copyFormStyleWithoutHeaderImage(sourceForm.formStyle),
        enabled: false,
        use: sourceForm.use ? { ...sourceForm.use } : {},
        activeStartDate: sourceForm.activeStartDate,
        activeEndDate: sourceForm.activeEndDate,
        parentIngestionPointId: sourceForm.ingestionPointId,
    };

    const newForm = await DataIngestionService.createIngestionPoint(copiedForm);

    if (!newForm.ingestionPointId) {
        throw new Error("Failed to copy form: new form ID is missing");
    }

    const copiedFieldDefinitions = copyFieldDefinitionsForNewForm(fieldDefinitions);
    if (copiedFieldDefinitions.length > 0) {
        await DataIngestionService.replaceFieldDefinitions(newForm.ingestionPointId, copiedFieldDefinitions);
    }

    await copyIngestionRulesForNewForm(sourceIngestionPointId, newForm.ingestionPointId);

    return newForm;
}
